import { useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Build compact page list: 1 … 8 9 10 … 100 (ellipsis gaps).
 * @param {number} current - 1-based
 * @param {number} total - total pages (>= 1)
 * @param {number} neighbors - pages on each side of current
 * @returns {(number|'ellipsis')[]}
 */
export function getSmartPageItems(current, total, neighbors = 2) {
    if (total <= 1) return [1];
    const set = new Set();
    set.add(1);
    set.add(total);
    for (let p = current - neighbors; p <= current + neighbors; p++) {
        if (p >= 1 && p <= total) set.add(p);
    }
    const sorted = [...set].sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push('ellipsis');
        out.push(sorted[i]);
    }
    return out;
}

/** Preset row counts tuned for large lists on modest hardware (small LIMIT/OFFSET per request). */
export const PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * @param {{
 *   currentPage: number,
 *   totalPages: number,
 *   totalRows: number,
 *   pageSize: number,
 *   onPageChange: (p: number) => void,
 *   onPageSizeChange?: (n: number) => void,
 *   disabled?: boolean,
 *   showPageSize?: boolean,
 *   className?: string,
 * }} props
 */
export default function SmartPagination({
    currentPage,
    totalPages,
    totalRows,
    pageSize,
    onPageChange,
    onPageSizeChange,
    disabled = false,
    showPageSize = true,
    className = '',
}) {
    const items = useMemo(
        () => getSmartPageItems(currentPage, Math.max(1, totalPages)),
        [currentPage, totalPages]
    );

    if (totalPages < 1 || totalRows === 0) return null;

    const startRow = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endRow = Math.min(currentPage * pageSize, totalRows);

    const btnBase =
        'min-w-[2.25rem] h-9 px-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-40 disabled:pointer-events-none';
    const btnIdle =
        'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700';
    const btnActive = 'border-indigo-500 bg-indigo-600 text-white dark:bg-indigo-600 dark:border-indigo-500';
    const btnNav = `${btnBase} ${btnIdle} inline-flex items-center justify-center gap-0.5`;

    return (
        <div
            className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-slate-100 dark:border-slate-800 ${className}`}
        >
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
                <span>
                    Showing{' '}
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{startRow}</span>
                    –
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{endRow}</span>
                    {' '}of{' '}
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{totalRows}</span>
                </span>
                {showPageSize && onPageSizeChange && (
                    <label className="inline-flex items-center gap-2">
                        <span className="text-slate-500">Rows</span>
                        <select
                            className="input h-9 py-0 text-sm w-[5.5rem]"
                            value={pageSize}
                            disabled={disabled}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        >
                            {PAGE_SIZE_OPTIONS.map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
            </div>

            {totalPages <= 1 ? null : (
                <nav className="flex flex-wrap items-center justify-center gap-1" aria-label="Pagination">
                    <button
                        type="button"
                        className={btnNav}
                        disabled={disabled || currentPage <= 1}
                        onClick={() => onPageChange(1)}
                        title="First page"
                    >
                        <ChevronsLeft className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        className={btnNav}
                        disabled={disabled || currentPage <= 1}
                        onClick={() => onPageChange(currentPage - 1)}
                        title="Previous page"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>

                    {items.map((item, idx) =>
                        item === 'ellipsis' ? (
                            <span
                                key={`ellipsis-${idx}`}
                                className="px-2 text-slate-400 dark:text-slate-500 select-none"
                                aria-hidden
                            >
                                …
                            </span>
                        ) : (
                            <button
                                key={item}
                                type="button"
                                className={`${btnBase} ${item === currentPage ? btnActive : btnIdle}`}
                                disabled={disabled}
                                onClick={() => onPageChange(item)}
                                aria-current={item === currentPage ? 'page' : undefined}
                            >
                                {item}
                            </button>
                        )
                    )}

                    <button
                        type="button"
                        className={btnNav}
                        disabled={disabled || currentPage >= totalPages}
                        onClick={() => onPageChange(currentPage + 1)}
                        title="Next page"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        className={btnNav}
                        disabled={disabled || currentPage >= totalPages}
                        onClick={() => onPageChange(totalPages)}
                        title="Last page"
                    >
                        <ChevronsRight className="h-4 w-4" />
                    </button>
                </nav>
            )}
        </div>
    );
}
